import { apiClient, v3Client, v3ODataClient } from '@api/apiClient';
import { PaginatedApiResponse, V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { ODataApiResponse } from '@typeDefinitions/orderTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

import {
  BillingQueueApprovedItem,
  BillingQueueDisputedItem,
  BillingQueuePendingApprovalItem,
  BillingQueueRespondedItem,
  BillingQueueUploadItem,
  DocumentMetaDataRecord,
  DocumentRecord,
  UploadedDocuments,
  ApprovedQueueResponseT,
  ApprovedQueueRowT,
  RespondedQueueResponseT,
  RespondedQueueRowT,
  UploadQueueRequestT,
  UploadQueueResponseT,
  UploadQueueRowT,
} from './types';

const isPortBillingApiEnabled = isFeatureFlagEnabled(
  FeatureFlags.PortBillingApi,
);
const isPortRemainingV3ApisEnabled = isFeatureFlagEnabled(
  FeatureFlags.PortRemainingV3Apis,
);

// API casing for the LTL flag has varied ('ltl' vs 'lTL') across serializer versions; accept either.
const normalizeLtlCasing = (
  item: Partial<BillingQueuePendingApprovalItem> & { ltl?: boolean },
): BillingQueuePendingApprovalItem => ({
  ...(item as BillingQueuePendingApprovalItem),
  lTL: item.lTL ?? item.ltl ?? false,
});

export const fetchBillingUploadQueueApi = async (payload: string) => {
  try {
    const data: unknown = await v3Client.get(
      `/Queue/Upload?AccountingGroupId=${payload}`,
    );
    return data as BillingQueueUploadItem[];
  } catch (e: unknown) {
    logError('fetchBillingUploadQueueApi', e as Error);
    throw createError(e, 'Failed to fetch billing upload data');
  }
};

export const fetchBillingUploadQueuePagedApi = async (
  accountingGroupId: string,
  payload: UploadQueueRequestT,
  isExport = false,
): Promise<UploadQueueResponseT> => {
  try {
    const params: Record<string, unknown> = {};
    if (accountingGroupId !== '') {
      params.accountingGroupId = Number(accountingGroupId);
    }
    if (isExport) {
      params.isExport = true;
    }

    const response: PaginatedApiResponse<UploadQueueRowT[]> =
      await apiClient.post('Queue/upload', payload, {
        params,
      });
    if (!response.isSuccess) {
      throw new Error(
        response.errors[0]?.message ?? 'Failed to fetch billing upload data',
      );
    }
    return {
      totalCount: response.value.totalCount,
      data: response.value.data.map(({ ltl, ...row }) => ({
        ...row,
        lTL: ltl,
      })),
    };
  } catch (e: unknown) {
    logError('fetchBillingUploadQueuePagedApi', e as Error);
    throw createError(e, 'Failed to fetch billing upload data');
  }
};

export const deleteBillingQueueFlag = async (id: number) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<unknown> = await apiClient.delete(
        `flag/${id}`,
        {
          data: {
            description: 'Removed from billing queue',
          },
        },
      );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to delete',
        );
      }
      return { success: response.isSuccess };
    }

    const response: { success: boolean } = await v3Client.delete(`flag/${id}`);
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to delete');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchBillingApprovedQueueApi = async (payload: string) => {
  try {
    const data: unknown = await v3Client.get(
      `/Queue/Approved?AccountingGroupId=${payload}`,
    );
    return data as BillingQueueApprovedItem[];
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch billing approved data');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchBillingApprovedQueuePagedApi = async (
  accountingGroupId: string,
  payload: UploadQueueRequestT,
  isExport = false,
): Promise<ApprovedQueueResponseT> => {
  try {
    const params: Record<string, unknown> = {};
    if (accountingGroupId !== '') {
      params.accountingGroupId = Number(accountingGroupId);
    }
    if (isExport) {
      params.isExport = true;
    }

    const response: PaginatedApiResponse<ApprovedQueueRowT[]> =
      await apiClient.post('Queue/approved', payload, {
        params,
      });
    if (!response.isSuccess) {
      throw new Error(
        response.errors[0]?.message ?? 'Failed to fetch billing approved data',
      );
    }
    return {
      totalCount: response.value.totalCount,
      data: response.value.data.map(({ ltl, ...row }) => ({
        ...row,
        lTL: ltl,
      })),
    };
  } catch (e: unknown) {
    logError('fetchBillingApprovedQueuePagedApi', e as Error);
    throw createError(e, 'Failed to fetch billing approved data');
  }
};

export const deleteBillingQueueApprovalFlag = async (id: number) => {
  try {
    const response: V4ApiResponse<unknown> = await apiClient.post(
      `Accounting/RemoveApprovalFlag/${id}`,
      { id },
    );
    if (!response.isSuccess) {
      handleToast(
        ToastType.Error,
        response.errors[0]?.message ?? 'Failed to delete',
      );
    }
    return { success: response.isSuccess };
  } catch (e: unknown) {
    const error = createError(e, 'Failed to delete');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchBillingPendingApprovalQueueApi = async (payload?: string) => {
  try {
    const accountingGroupQuery = payload ? `?AccountingGroupId=${payload}` : '';
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<BillingQueuePendingApprovalItem[]> =
        await apiClient.get(`/Queue/PendingApprovals${accountingGroupQuery}`);
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ??
            'Failed to fetch pending approval data',
        );
        return [];
      }
      return response.value.map(normalizeLtlCasing);
    }

    const data: unknown = await v3Client.get(
      `/Queue/PendingApprovals${accountingGroupQuery}`,
    );
    return (data as BillingQueuePendingApprovalItem[]).map(normalizeLtlCasing);
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch pending approval data');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchLoadDocumentApi = async () => {
  try {
    if (isPortRemainingV3ApisEnabled()) {
      const response: V4ApiResponse<DocumentRecord[]> =
        await apiClient.get('LoadDocument');
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to fetch loadDocument data',
        );
        return;
      }
      return response.value;
    }

    const data: unknown = await v3Client.get('LoadDocument');
    return data as DocumentRecord[];
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch loadDocument data');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchLoadDocumentByIdApi = async (id: string) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<DocumentMetaDataRecord> =
        await apiClient.get(`LoadDocument/${id}`);
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ??
            'Failed to fetch loadDocument by id data',
        );
        return;
      }
      return response.value;
    }

    const data: DocumentMetaDataRecord = await v3Client.get(
      `LoadDocument/${id}`,
    );
    return data;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch loadDocument by id data');
    handleToast(ToastType.Error, error.message);
  }
};

export const reImportBatchApi = async (batchId: string) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<boolean | null> = await apiClient.post(
        `LoadDocument/Reimport/${batchId}`,
      );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to re-import batch',
        );
      }
      return { success: response.isSuccess && response.value !== false };
    }

    const data: { success: boolean } = await v3Client.post(
      `LoadDocument/Reimport/${batchId}`,
    );
    return data;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to re-import batch');
    handleToast(ToastType.Error, error.message);
  }
};

export const deleteLoadDocumentApi = async (id: number) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<boolean | null> = await apiClient.delete(
        `LoadDocument/${id}`,
      );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to delete load document',
        );
      }
      return { success: response.isSuccess && response.value !== false };
    }

    const response: { success: boolean } = await v3Client.delete(
      `LoadDocument/${id}`,
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to delete load document');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchBillingRespondedQueueApi = async (payload: string) => {
  try {
    const data: unknown = await v3Client.get(
      `/Queue/Responded?AccountingGroupId=${payload}`,
    );
    return data as BillingQueueRespondedItem[];
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch billing responded data');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchBillingRespondedQueuePagedApi = async (
  accountingGroupId: string,
  payload: UploadQueueRequestT,
  isExport = false,
): Promise<RespondedQueueResponseT> => {
  try {
    const params: Record<string, unknown> = {};
    if (accountingGroupId !== '') {
      params.accountingGroupId = Number(accountingGroupId);
    }
    if (isExport) {
      params.isExport = true;
    }

    const response: PaginatedApiResponse<RespondedQueueRowT[]> =
      await apiClient.post('Queue/responded', payload, {
        params,
      });
    if (!response.isSuccess) {
      throw new Error(
        response.errors[0]?.message ?? 'Failed to fetch billing responded data',
      );
    }
    return {
      totalCount: response.value.totalCount,
      data: response.value.data.map(({ ltl, ...row }) => ({
        ...row,
        lTL: ltl,
      })),
    };
  } catch (e: unknown) {
    logError('fetchBillingRespondedQueuePagedApi', e as Error);
    throw createError(e, 'Failed to fetch billing responded data');
  }
};

export const moveBatchApi = async (payload: {
  batchId: string;
  loadNumber: string;
}) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<number | null> = await apiClient.put(
        'LoadDocument/MoveBatch/',
        payload,
      );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to move batch',
        );
        return;
      }
      return {
        success: response.isSuccess,
        data: response.value,
      };
    }

    const data: { success: boolean; data: number } = await v3Client.put(
      'LoadDocument/MoveBatch/',
      payload,
    );
    return data;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to move batch');
    handleToast(ToastType.Error, error.message);
  }
};

export const markLoadQuickpayApi = async (payload: {
  quickPay: boolean;
  loadNumber: string;
}) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<{ success: boolean; data: string }> =
        await apiClient.post('Load/MarkLoadQuickpay/', payload);
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to mark load as quickpay',
        );
        return;
      }
      return response.value;
    }

    const response: { success: boolean; data: string } = await v3Client.post(
      'Load/MarkLoadQuickpay/',
      payload,
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to mark load as quickpay');
    handleToast(ToastType.Error, error.message);
  }
};

export const addToBillingQueueApi = async (payload: {
  quickPay: boolean;
  loadNumber: string;
}) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<unknown> = await apiClient.get(
        'LoadDocument/AddToBillingQueue/',
        {
          params: payload,
        },
      );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to add to billing queue',
        );
        return;
      }
      return {
        success: response.isSuccess,
        data: response.value,
      };
    }

    const response: { success: boolean; data: string } = await v3Client.get(
      `LoadDocument/AddToBillingQueue/?loadNumber=${payload.loadNumber}&quickPay=${payload.quickPay}`,
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to add to billing queue');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchBillingDisputedQueueApi = async (payload: string) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<BillingQueueDisputedItem[]> =
        await apiClient.get(
          `/Queue/DisputedOrders?AccountingGroupId=${payload}`,
        );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ??
            'Failed to fetch billing disputed data',
        );
        return [];
      }
      return response.value;
    }

    const data: unknown = await v3Client.get(
      `/Queue/DisputedOrders?AccountingGroupId=${payload}`,
    );
    return data as BillingQueueDisputedItem[];
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch billing disputed data');
    handleToast(ToastType.Error, error.message);
  }
};

export async function fetchDocumentHistoryQueueApi(queryParams: {
  $format?: string;
  $top?: number;
  $skip?: number;
  $orderby?: string;
  $count?: boolean;
  $filter?: string;
}) {
  try {
    const response: ODataApiResponse = isPortBillingApiEnabled()
      ? await apiClient.post('TransfloHistory', queryParams)
      : await v3ODataClient.get('TransfloHistory', {
          params: queryParams,
        });

    if ('value' in response) {
      return {
        data: response.value,
        count: response['@odata.count'],
      };
    }
    handleToast(ToastType.Error, 'Failed to fetch document history data');
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch document history data');
    handleToast(ToastType.Error, error.message);
  }
}

export const reimportDocumentApi = async (payload: string) => {
  try {
    if (isPortRemainingV3ApisEnabled()) {
      const response: V4ApiResponse<boolean | null> = await apiClient.post(
        `/LoadDocument/Reimport/${payload}`,
        { batchId: payload },
      );
      if (Boolean(response.isSuccess)) {
        handleToast(ToastType.Success, 'Batch queued to re-import');
      }
      return;
    }

    const data: {
      success: boolean;
      data: null;
      message: string;
    } = await v3Client.post(`/LoadDocument/Reimport/${payload}`, {
      batchId: payload,
    });
    if (data.success) {
      handleToast(ToastType.Success, 'Batch queued to re-import');
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to reimport document');
    handleToast(ToastType.Error, error.message);
  }
};

export const deleteDocumentApi = async (payload: {
  loadNumber: string;
  page: string;
}) => {
  try {
    if (isPortRemainingV3ApisEnabled()) {
      const response: V4ApiResponse<boolean | null> = await apiClient.delete(
        'LoadDocument/RemoveDocument',
        { data: payload },
      );
      if (Boolean(response.isSuccess)) {
        handleToast(ToastType.Success, 'Document deleted successfully');
      }
      return;
    }

    const data: {
      success: boolean;
      data: null;
      message: string;
    } = await v3Client.delete('LoadDocument/RemoveDocument', {
      data: payload,
    });
    if (data.success) {
      handleToast(ToastType.Success, 'Document deleted successfully');
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to delete document');
    handleToast(ToastType.Error, error.message);
  }
};

export const moveUnkownDocument = async (payload: {
  docType: string;
  file: string;
  batchId: string;
}) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<{ newUrl: string }> = await apiClient.put(
        '/LoadDocument/MoveUnknownDocument',
        payload,
      );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to move document',
        );
        return;
      }

      handleToast(ToastType.Success, 'Document moved successfully');
      return {
        success: response.isSuccess,
        data: response.value,
        message: '',
      };
    }

    const data: {
      success: boolean;
      data: { newUrl: string };
      message: string;
    } = await v3Client.put('/LoadDocument/MoveUnknownDocument', {
      ...payload,
    });
    if (data.success) {
      handleToast(ToastType.Success, 'Document moved successfully');
      return data;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to move document');
    handleToast(ToastType.Error, error.message);
  }
};

export const loadDocumentUpdate = async (payload: {
  docType: string;
  file: string;
  loadNumber: string;
}) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<{ newUrl: string }> = await apiClient.put(
        '/LoadDocument/MoveStagingDocument',
        payload,
      );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ?? 'Failed to move document',
        );
        return;
      }

      handleToast(ToastType.Success, 'Document moved successfully');
      return {
        success: response.isSuccess,
        data: response.value,
        message: '',
      };
    }

    const data: {
      success: boolean;
      data: { newUrl: string };
      message: string;
    } = await v3Client.put(`/LoadDocument/${payload.loadNumber}`, {
      ...payload,
    });
    if (data.success) {
      handleToast(ToastType.Success, 'Document moved successfully');
      return data;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to move document');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchLoadDocumentByBatchApi = async (id: string) => {
  try {
    if (isPortBillingApiEnabled()) {
      const response: V4ApiResponse<UploadedDocuments> = await apiClient.get(
        `LoadDocument/ByBatch/${id}`,
      );
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ??
            'Failed to fetch loadDocument by batch data',
        );
        return;
      }
      return response.value;
    }

    const data: UploadedDocuments = await v3Client.get(
      `LoadDocument/ByBatch/${id}`,
    );
    return data;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch loadDocument by batch data');
    handleToast(ToastType.Error, error.message);
  }
};
